import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Matches the breakpoint used by the section's stylesheet. Above it the section is
 * always the desktop 2-column layout and the grid/browse modes do not exist.
 */
const MOBILE_QUERY = '(max-width: 1079px)';

const ITEM_SELECTOR = '.lookbook-images__item';

/** Slide timings for the grid <-> browse transition. */
const SLIDE_DURATION = 180;
const SLIDE_EASING = 'cubic-bezier(0.25, 1, 0.5, 1)';

/** Where the incoming layout slides in from: right when going deeper, left when going back. */
const FROM_RIGHT = '100%';
const FROM_LEFT = '-100%';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} [inner] - Bar + grid; the panel that goes full-screen in browse.
 * @property {HTMLElement} [bar] - The title bar.
 * @property {HTMLElement} [grid] - The container holding every item; the pager in browse.
 * @property {HTMLElement[]} [items] - The lookbook items, in document order.
 */

/**
 * Two mobile views over the same markup, toggled with `data-view`:
 * `grid` (2 columns, natural aspect ratio) and `browse` (full-screen vertical pager).
 *
 * Only the attribute changes — DOM order is untouched, so switching re-fetches no images and
 * the section stays safe to re-render server-side.
 *
 * @extends {Component<Refs>}
 */
class LookbookImages extends Component {
  #mediaQuery = matchMedia(MOBILE_QUERY);

  /** @type {Animation | undefined} The in-flight view slide, if any. */
  #slide;

  /** @type {HTMLElement | undefined} The look the pinned Shop the look button sits over. */
  #currentItem;

  /** @type {number | undefined} A pending frame for the scroll-driven `#currentItem` update. */
  #frame;

  connectedCallback() {
    super.connectedCallback();
    this.#mediaQuery.addEventListener('change', this.#handleBreakpointChange);
    this.#syncCurrentItemTracking();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#mediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#stopTrackingCurrentItem();
    // Cancelling rejects `finished`, which runs the slide's cleanup (freeze layer, attribute).
    this.#slide?.cancel();
  }

  /**
   * Opens the full-screen pager at the tapped look. Wired up via
   * `on:click="/enterBrowse"` on each item's tap target.
   *
   * @param {Event} event
   */
  enterBrowse(event) {
    if (!this.#mediaQuery.matches) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const item = target.closest(ITEM_SELECTOR);
    if (!(item instanceof HTMLElement)) return;

    // Capture the grid before the relayout, so the incoming panel has it to slide over.
    const freeze = this.#freezeViewport();

    this.dataset.view = 'browse';
    this.#pageToItem(item);
    this.#syncCurrentItemTracking();

    // The whole panel travels in browse — it's the full-screen overlay, bar included.
    this.#slideIn(this.refs.inner, FROM_RIGHT, freeze);
  }

  /**
   * Returns to the grid, keeping the look that was being paged through in place so the
   * shopper lands where they left off rather than at the top of the page. Wired up via
   * `on:click="/exitBrowse"` on the bar's back button.
   */
  exitBrowse() {
    const item = this.#pagedItem();
    const freeze = this.#freezeViewport();

    this.dataset.view = 'grid';

    if (item) this.#scrollToItem(item);

    this.#syncCurrentItemTracking();

    // Back in the page's own flow, the grid is the only thing that moves.
    this.#slideIn(this.refs.grid, FROM_LEFT, freeze);
  }

  /**
   * Slides the newly laid-out `panel` in from `origin`, over a still image of the outgoing view.
   *
   * Both views share one set of nodes, so the outgoing layout can't stay on screen by itself —
   * `freeze` captures it into a throwaway layer the incoming panel travels over, so the slide
   * never reveals the page background.
   *
   * Purely visual: `data-view` and scroll position are final before it starts, so an interrupted
   * or unsupported slide still leaves correct state.
   *
   * @param {HTMLElement | undefined} panel - The incoming view's outermost element.
   * @param {string} origin - A translateX value to start from.
   * @param {HTMLElement | null} freeze - The outgoing view's still image, if captured.
   */
  #slideIn(panel, origin, freeze) {
    if (!panel || !this.#mediaQuery.matches || prefersReducedMotion()) {
      freeze?.remove();
      return;
    }

    // A second tap mid-slide should retarget, not queue up behind the first.
    this.#slide?.cancel();

    const animation = panel.animate(
      { transform: [`translateX(${origin})`, 'translateX(0)'] },
      { duration: SLIDE_DURATION, easing: SLIDE_EASING },
    );

    this.#slide = animation;

    // Lifts the real content over the still image, and parks the pinned button, which
    // would otherwise position itself against the transformed panel. See the stylesheet.
    this.dataset.sliding = '';

    const cleanup = () => {
      // This layer belongs to this slide, so it always goes.
      freeze?.remove();

      // Cancelling above rejects the previous slide's promise, and that runs after
      // this one has started — so only the current slide may drop the shared state.
      if (this.#slide !== animation) return;

      delete this.dataset.sliding;
      this.#slide = undefined;
    };

    animation.finished.then(cleanup).catch(cleanup);
  }

  /**
   * Copies on-screen images into a fixed, inert layer pinned at their viewport positions — a
   * cheap stand-in for the outgoing view that survives the relayout underneath it.
   *
   * Copies reuse the original `srcset`, so the browser serves the already-decoded candidate
   * from cache and makes no extra requests.
   *
   * @returns {HTMLElement | null}
   */
  #freezeViewport() {
    if (!this.#mediaQuery.matches || prefersReducedMotion()) return null;

    const layer = document.createElement('div');
    layer.className = 'lookbook-images__freeze';
    layer.setAttribute('aria-hidden', 'true');

    for (const item of this.#items) {
      const rect = item.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= window.innerHeight) continue;

      // For video blocks this is the poster; blocks with neither are simply skipped.
      const image = item.querySelector('img');
      if (!(image instanceof HTMLImageElement)) continue;

      const copy = /** @type {HTMLImageElement} */ (image.cloneNode(false));
      copy.loading = 'eager';
      copy.style.cssText = `position:absolute;top:${rect.top}px;left:${rect.left}px;width:${rect.width}px;height:${rect.height}px;object-fit:cover;`;
      layer.append(copy);
    }

    if (!layer.children.length) return null;

    document.body.append(layer);

    return layer;
  }

  /**
   * Aligns an item with the bottom of the sticky bar. Reading the item's box
   * after `data-view` changed forces the new layout to be resolved first, so
   * this is accurate across a view switch.
   *
   * @param {HTMLElement} item
   */
  #scrollToItem(item) {
    const top = item.getBoundingClientRect().top + window.scrollY - this.#stickyOffset();

    // `instant` because `html` sets `scroll-behavior: smooth` globally, which would
    // otherwise animate a jump across the whole (re-laid-out) page.
    window.scrollTo({ top, behavior: 'instant' });
  }

  /**
   * Pages the browse view to a look. The pager is its own fixed, screen-height scroll
   * container, so this is not the page-level `#scrollToItem`.
   *
   * Boxes are read after `data-view` changes so the offset measures against full-height
   * looks. A look's top edge is also its snap point, so the pager settles where it's put.
   *
   * @param {HTMLElement} item
   */
  #pageToItem(item) {
    const { grid } = this.refs;

    if (!grid) return;

    const top =
      grid.scrollTop + item.getBoundingClientRect().top - grid.getBoundingClientRect().top;

    grid.scrollTo({ top, behavior: 'instant' });
  }

  /**
   * The look the pager is on: the one lying across its middle — mid-swipe, the one the snap
   * is settling on. Judging by the pinned button's own position instead would hand the button
   * over ~30px into a swipe, while the previous look still filled the screen.
   *
   * @returns {HTMLElement | undefined}
   */
  #pagedItem() {
    const { grid } = this.refs;

    if (!grid) return undefined;

    const pager = grid.getBoundingClientRect();
    const middle = (pager.top + pager.bottom) / 2;

    return this.#items.find((item) => {
      const box = item.getBoundingClientRect();

      return box.top <= middle && box.bottom > middle;
    });
  }

  /**
   * Browse view pins one "Shop the look" button to the bottom instead of one per look, so the
   * current look must be tracked as the shopper swipes. That's mobile-pager-only, so the
   * listeners live and die with it and sit on the pager, which is what scrolls there.
   *
   * Safe to call on every state change — re-adding the same listener is a no-op, so this just
   * reflects `data-view` and the breakpoint.
   */
  #syncCurrentItemTracking() {
    if (this.dataset.view !== 'browse' || !this.#mediaQuery.matches) {
      this.#stopTrackingCurrentItem();
      return;
    }

    this.refs.grid?.addEventListener('scroll', this.#handleScroll, { passive: true });
    window.addEventListener('resize', this.#handleScroll, { passive: true });
    this.#setCurrentItem(this.#pagedItem());
  }

  #stopTrackingCurrentItem() {
    this.refs.grid?.removeEventListener('scroll', this.#handleScroll);
    window.removeEventListener('resize', this.#handleScroll);

    if (this.#frame != null) cancelAnimationFrame(this.#frame);
    this.#frame = undefined;

    this.#setCurrentItem(undefined);
  }

  /** Coalesces scroll and resize events into one measurement per frame. */
  #handleScroll = () => {
    if (this.#frame != null) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = undefined;
      this.#setCurrentItem(this.#pagedItem());
    });
  };

  /**
   * Hands the pinned button to the look the pager is on. A look without products has no
   * button to hand over, which correctly leaves the screen without one.
   *
   * `data-current` is what the stylesheet keys the pinned button off, so exactly one item
   * may carry it at a time.
   *
   * @param {HTMLElement | undefined} item
   */
  #setCurrentItem(item) {
    if (this.#currentItem === item) return;

    if (this.#currentItem) delete this.#currentItem.dataset.current;
    if (item) item.dataset.current = 'true';

    this.#currentItem = item;
  }

  /** @returns {HTMLElement[]} */
  get #items() {
    const { items } = this.refs;

    return items ?? [];
  }

  /**
   * Height of everything pinned to the top: the sticky bar plus its own offset.
   *
   * The offset comes from the bar's resolved `top` rather than the header height, because the
   * stylesheet drops it to 0 when the header isn't pinned — assuming otherwise leaves a gap.
   *
   * @returns {number}
   */
  #stickyOffset() {
    const { bar } = this.refs;

    if (!bar) return 0;

    // `auto` on desktop, where the bar isn't sticky.
    return (parseFloat(getComputedStyle(bar).top) || 0) + bar.offsetHeight;
  }

  /**
   * The browse view only exists on mobile — if the viewport grows past the
   * breakpoint (rotation, desktop resize) the shopper would otherwise be left in
   * a state with no way back, since the back button is hidden on desktop.
   */
  #handleBreakpointChange = () => {
    if (!this.#mediaQuery.matches) this.dataset.view = 'grid';

    this.#syncCurrentItemTracking();
  };
}

if (!customElements.get('lookbook-images-component')) {
  customElements.define('lookbook-images-component', LookbookImages);
}
